/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { uniq } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import {
  Button,
  Grid,
  IconButton,
  InlineNotification,
  Skeleton,
  Typography,
  Typography2,
  VisuallyHidden,
} from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { AddIcon, LaptopIcon, LocationIcon, UserIcon } from '@coursera/cds-icons';

import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import EditWorkPreferencesModal from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-preferences/EditWorkPreferencesModal';
import RestrictHiringSolutionsGate from 'bundles/account-profile/components/recruiting-profile-viewer/utils/RestrictHiringSolutionsGate';
import { getIsUSOrIndia } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/countryUtils';
import { getRolesAndPreferences } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  customCss?: Interpolation<Theme>;
};

type Props = PropsFromCaller;

const E2E_SELECTORS = {
  WORK_PREF_SECTION: 'profile-work-pref-section',
  WORK_PREF_SECTION_EDIT: 'profile-work-pref-edit',
} as const;

const styles = {
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  editIcon: css`
    margin: calc(var(--cds-spacing-50) * -1) calc(var(--cds-spacing-200)} * -1) calc(var(--cds-spacing-50) * -1) 0;
    width: auto;
    align-self: flex-start;
  `,
  rowIcon: css`
    margin-right: var(--cds-spacing-150);
  `,
  sectionGrouping: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  rowGrouping: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  roleName: css`
    margin-right: var(--cds-spacing-50);
  `,
  emptyStateText: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  sectionTitleContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
};

const WorkPreferencesBodyPlaceholder = () => {
  return (
    <div role="status">
      <Typography variant="h2semibold" data-testid="work-preferences-section-title" css={styles.sectionTitle}>
        {_t('Work preferences')}
      </Typography>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionGrouping}>
        {_t('Desired roles')}
      </Typography2>
      <div css={styles.sectionGrouping}>
        <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
          <UserIcon size="medium" color="support" css={styles.rowIcon} />
          <Grid item xs={10} sm={11} md={10}>
            <Typography2 component="p" variant="bodyPrimary">
              <Skeleton />
            </Typography2>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
          <UserIcon size="medium" color="support" css={styles.rowIcon} />
          <Grid item xs={10} sm={11} md={10}>
            <Typography2 component="p" variant="bodyPrimary">
              <Skeleton />
            </Typography2>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
          <UserIcon size="medium" color="support" css={styles.rowIcon} />
          <Grid item xs={10} sm={11} md={10}>
            <Typography2 component="p" variant="bodyPrimary">
              <Skeleton />
            </Typography2>
          </Grid>
        </Grid>
      </div>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionGrouping}>
        {_t('Location preferences')}
      </Typography2>
      <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
        <LaptopIcon size="medium" color="support" css={styles.rowIcon} />
        <Grid item xs={10} sm={11} md={10}>
          <Typography2 component="p" variant="bodyPrimary">
            <Skeleton />
          </Typography2>
        </Grid>
      </Grid>
      <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
        <LocationIcon size="medium" color="support" css={styles.rowIcon} />
        <Grid item xs={10} sm={11} md={10}>
          <Typography2 component="p" variant="bodyPrimary">
            <Skeleton />
          </Typography2>
        </Grid>
      </Grid>
    </div>
  );
};

const WorkPreferencesSection = ({ customCss }: Props) => {
  const [shouldShowEditModal, setShouldShowEditModal] = useState<boolean>(false);
  const { isProfileLoading, profileState = {}, editable } = useContext(ProfileContext);
  const { isRolesMapLoading, idToRolesMap } = useContext(OccupationRolesContext);
  const { openToRemoteWork, willingToRelocate, occupationPreferences = [] } = profileState;

  if (isProfileLoading || isRolesMapLoading) {
    return (
      <ProfileViewerCard customCss={customCss}>
        <WorkPreferencesBodyPlaceholder />
      </ProfileViewerCard>
    );
  }

  const inferredUserLocationCountryCode = profileState.inferredUserLocationCountryCode;
  const isUSOrIndia = getIsUSOrIndia(inferredUserLocationCountryCode, profileState.location?.countryName);

  const rolesAndPreferences = getRolesAndPreferences(occupationPreferences, idToRolesMap);
  const isOpenToRemoteWorkDefined = isUSOrIndia && openToRemoteWork !== undefined && openToRemoteWork !== null;
  const isWillingToRelocateDefined = isUSOrIndia && willingToRelocate !== undefined && willingToRelocate !== null;

  if (occupationPreferences.length === 0 && !isOpenToRemoteWorkDefined && !isWillingToRelocateDefined && !editable) {
    return null;
  } else if (!occupationPreferences.length && !isOpenToRemoteWorkDefined && !isWillingToRelocateDefined && editable) {
    return (
      <ProfileViewerCard customCss={customCss} data-e2e={E2E_SELECTORS.WORK_PREF_SECTION}>
        <VisuallyHidden component="h2" className="rc-A11yScreenReaderOnly">
          {_t('Work preferences')}
        </VisuallyHidden>
        <Typography2 component="p" color="supportText" css={styles.emptyStateText}>
          {_t(
            'Let recruiters know what role you’re looking for to make sure you find opportunities that are right for you.'
          )}
        </Typography2>
        <Button
          variant="secondary"
          iconPosition="before"
          onClick={() => setShouldShowEditModal(true)}
          icon={<AddIcon />}
          component={TrackedButton}
          // not tracked in eventing v3
          data-e2e={E2E_SELECTORS.WORK_PREF_SECTION_EDIT}
          trackingName="profile_add_work_preferences"
          requireFullyVisible={false}
          withVisibilityTracking={true}
          size="small"
        >
          {_t('Add work preferences')}
        </Button>
        <EditWorkPreferencesModal isOpen={shouldShowEditModal} onClose={() => setShouldShowEditModal(false)} />
      </ProfileViewerCard>
    );
  } else {
    return (
      <ProfileViewerCard customCss={customCss} data-e2e={E2E_SELECTORS.WORK_PREF_SECTION}>
        <div css={styles.sectionTitleContainer}>
          <Typography variant="h2semibold" data-testid="work-preferences-section-title" css={styles.sectionTitle}>
            {_t('Work preferences')}
          </Typography>
          {editable && (
            <>
              <EditWorkPreferencesModal isOpen={shouldShowEditModal} onClose={() => setShouldShowEditModal(false)} />
              <Grid container direction="row" justifyContent="flex-end" css={styles.editIcon}>
                <IconButton
                  variant="ghost"
                  intent="edit"
                  size="small"
                  onClick={() => setShouldShowEditModal(true)}
                  data-e2e={E2E_SELECTORS.WORK_PREF_SECTION_EDIT}
                  component={TrackedButton}
                  // not tracked in eventing v3
                  trackingName="profile_edit_work_preferences"
                  requireFullyVisible={false}
                  withVisibilityTracking={true}
                />
              </Grid>
            </>
          )}
        </div>
        {occupationPreferences.length > 0 && (
          <div css={styles.sectionGrouping}>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionGrouping}>
              {_t('Desired roles')}
            </Typography2>
            {Object.entries(rolesAndPreferences).map(([name, levels]) => (
              <div key={name}>
                {name.trim() && (
                  <Grid container direction="row" alignItems="center" css={styles.rowGrouping} wrap="nowrap">
                    <UserIcon size="medium" color="support" css={styles.rowIcon} />
                    <Grid container item direction="row" xs={11} wrap="wrap">
                      <Typography2 component="p" variant="bodyPrimary" css={styles.roleName}>
                        {name}
                      </Typography2>
                      {levels.length > 0 && (
                        <Typography2 component="p" variant="bodyPrimary" data-testid="all-levels-text">
                          <FormattedMessage
                            message={_t('({allDesiredRoleLevels})')}
                            allDesiredRoleLevels={uniq(levels).join(', ')}
                          />
                        </Typography2>
                      )}
                    </Grid>
                  </Grid>
                )}
              </div>
            ))}
          </div>
        )}
        {(isOpenToRemoteWorkDefined || isWillingToRelocateDefined) && (
          <>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionGrouping}>
              {_t('Location preferences')}
            </Typography2>
            {isOpenToRemoteWorkDefined && (
              <>
                <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
                  <LaptopIcon size="medium" color="support" css={styles.rowIcon} />
                  <Typography2 component="p" variant="bodyPrimary">
                    {openToRemoteWork ? _t('Open to remote work') : _t('Not open to remote work')}
                  </Typography2>
                </Grid>
              </>
            )}
            {isWillingToRelocateDefined && (
              <>
                <Grid container direction="row" alignItems="center" css={styles.rowGrouping}>
                  <LocationIcon size="medium" color="support" css={styles.rowIcon} />
                  <Typography2 component="p" variant="bodyPrimary">
                    {willingToRelocate ? _t('Willing to relocate') : _t('Not willing to relocate')}
                  </Typography2>
                </Grid>
              </>
            )}
          </>
        )}
      </ProfileViewerCard>
    );
  }
};

const ErrorBoundWorkPreferencesSection = (props: Props) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="WorkPreferencesSection"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the work preferences section. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <RestrictHiringSolutionsGate>
        <WorkPreferencesSection {...props} />
      </RestrictHiringSolutionsGate>
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundWorkPreferencesSection;
